/**
 * Scene Editor Page
 * Feature: Scene Editor Redesign
 *
 * Redesigned page with full-width header, scrollable scene list,
 * and inline AI chat column.
 * Route: /reel/:reelId
 */

import { useParams, useNavigate } from 'react-router-dom';
import { Clapperboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SceneEditorContainer } from '@/components/reels/SceneEditorContainer';
import { useReelById } from '@/hooks/useReelGeneration';
import type { Id } from '../../convex/_generated/dataModel';
import type { SceneSplitStatus } from '@/@types/reelScenes';

function PageLoadingSkeleton() {
  return (
    <div className="flex h-screen flex-col">
      {/* Header Skeleton */}
      <div className="border-b border-border bg-background-secondary px-8 py-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
        <div className="mt-3 flex gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-36" />
          <Skeleton className="h-8 w-32" />
        </div>
      </div>
      {/* Content Skeleton */}
      <div className="flex flex-1 gap-4 overflow-hidden p-4">
        <div className="flex-1 space-y-3">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
        <Skeleton className="h-full w-[380px] rounded-xl" />
      </div>
    </div>
  );
}

function ReelNotFound({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-8 text-center">
        <Clapperboard className="mx-auto h-12 w-12 text-red-400" />
        <h2 className="mt-4 text-xl font-semibold text-red-900">
          Reel Not Found
        </h2>
        <p className="mt-2 text-sm text-red-700">
          The requested reel could not be found or you don&apos;t have access to
          it.
        </p>
        <Button onClick={onBack} className="mt-6">
          Go Back
        </Button>
      </div>
    </div>
  );
}

function SceneEditorPage() {
  const { reelId } = useParams<{ reelId: string }>();
  const navigate = useNavigate();

  const { reel, isLoading, notFound } = useReelById(
    reelId as Id<'reels'> | undefined
  );

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return <PageLoadingSkeleton />;
  }

  if (notFound || !reel) {
    return <ReelNotFound onBack={() => navigate(-1)} />;
  }

  return (
    <SceneEditorContainer
      reelId={reelId as Id<'reels'>}
      sceneSplitStatus={reel.sceneSplitStatus as SceneSplitStatus | undefined}
      reelTitle={reel.brandName ?? 'Reel'}
      onBack={handleBack}
    />
  );
}

export { SceneEditorPage };
